// Functions was copied from here https://www.webtoolkit.info/javascript_color_conversion.html
// TODO: REVIEW - long term we should probably move to using npm lib 'color-convert' instead, to have standardized functions for all conversions - instead our semi-homegrown solution
import lodashIsFinite from 'lodash/isFinite';
import type { BrandColorValues, ColorDto } from '@/ApiClient';

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Cmyk {
  c: number;
  m: number;
  y: number;
  k: number;
}

export interface Hsv {
  h: number;
  s: number;
  v: number;
}

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

// ============================================================================
// ============================================================================
// Factories
// const RGB = function (r: number, g: number, b: number): Rgb {
//   if (r <= 0) {
//     r = 0;
//   }
//   if (g <= 0) {
//     g = 0;
//   }
//   if (b <= 0) {
//     b = 0;
//   }

//   if (r > 255) {
//     r = 255;
//   }
//   if (g > 255) {
//     g = 255;
//   }
//   if (b > 255) {
//     b = 255;
//   }

//   return {
//     r,
//     g,
//     b,
//   };
// };

// const HSV = function (h: number, s: number, v: number): Hsv {
//   if (h <= 0) {
//     h = 0;
//   }
//   if (s <= 0) {
//     s = 0;
//   }
//   if (v <= 0) {
//     v = 0;
//   }

//   if (h > 360) {
//     h = 360;
//   }
//   if (s > 100) {
//     s = 100;
//   }
//   if (v > 100) {
//     v = 100;
//   }

//   return {
//     h,
//     s,
//     v,
//   };
// };

const CMYK = function (c: number, m: number, y: number, k: number): Cmyk {
  if (c <= 0) {
    c = 0;
  }
  if (m <= 0) {
    m = 0;
  }
  if (y <= 0) {
    y = 0;
  }
  if (k <= 0) {
    k = 0;
  }

  if (c > 100) {
    c = 100;
  }
  if (m > 100) {
    m = 100;
  }
  if (y > 100) {
    y = 100;
  }
  if (k > 100) {
    k = 100;
  }

  return {
    c,
    m,
    y,
    k,
  };
};

// ============================================================================
// ============================================================================
// Calculate Color Brightness
export const calculateColorBrightness = (color: string | Rgb | any) => {
  // convert "rgb(255,255,255)" notation to {r:255,g:255,b:255} object
  if (typeof color === 'string' && color.trim().startsWith('rgb(')) {
    // @ts-ignore Not worth it right now. Assumes 'rgb(r, g, b)' format.
    const [r, g, b] = color.match(/\d+/g); // See https://stackoverflow.com/a/3752026/856559 for reference
    color = { r: +r, g: +g, b: +b };
  }

  const colorProfile = defineColorProfile(color);

  if (colorProfile !== 'rgb' && colorProfile !== 'rgbHex3' && colorProfile !== 'rgbHex6') {
    console.error('Unknown color profile ', colorProfile);
    return null;
  }

  if (colorProfile === 'rgbHex3' || colorProfile === 'rgbHex6') {
    color = convertHEXtoRGB(color);
  }

  // 3rd option of accepted answer here: https://stackoverflow.com/questions/596216/formula-to-determine-brightness-of-rgb-color
  return Math.round(Math.sqrt(color.r * color.r * 0.299 + color.g * color.g * 0.587 + color.b * color.b * 0.114));

  // Old one - unclear source
  // return Math.round(Math.sqrt(color.r * color.r * .241 + color.g * color.g * .691 + color.b * color.b * .068));
};

// Returns 'white' or a dark color for readable text on a given background color.
export const textColorForBackground = (bgColor: string | undefined): string => {
  if (!bgColor) return 'white';
  const brightness = calculateColorBrightness(bgColor);
  return brightness !== null && brightness > 205 ? '#35495e' : 'white';
};

// ===========================================================================
// ===========================================================================
// Define Color Profile
export const defineColorProfile = (color: string | Rgb | Cmyk | Hsv | Hsl | any) => {
  // Formats: f0f, #f0f, ff00ff, #ff00ff, {r:10,g:10,b:10}, {h:10,s:10,l:10}, {h:10,s:10,v:10}, {c:10,m:10,y:10,k:10}

  const isObject = typeof color === 'object';
  let isHexRgb;
  let isHexCodeLength3;
  let isHexCodeLength6;

  // ==================================
  // Check if color is a valid hex rgb
  if (typeof color !== 'string') {
    isHexRgb = false;
  } else {
    color = color.trim().replace(/#/, '');
    let normalizedColor;
    if (color.length === 3)
      normalizedColor = color
        .split('')
        .map((color: any) => color + color)
        .join('');
    isHexRgb = normalizedColor ? !!normalizedColor.match(/[0-9a-f]{6}/i) : !!color.match(/[0-9a-f]{6}/i);
  }

  // Define hex code length
  let refinedString;
  if (isHexRgb) {
    refinedString = color.trim().replace(/#/, '');
    if (refinedString.length === 3) isHexCodeLength3 = true;
    if (refinedString.length === 6) isHexCodeLength6 = true;
  }
  // ==================================

  const r = color.r !== undefined && color.r !== null && color.r !== '';
  const g = color.g !== undefined && color.g !== null && color.g !== '';
  const b = color.b !== undefined && color.b !== null && color.b !== '';

  const h = color.h !== undefined && color.h !== null && color.h !== '';
  const s = color.s !== undefined && color.s !== null && color.s !== '';
  const l = color.l !== undefined && color.l !== null && color.l !== '';
  const v = color.v !== undefined && color.v !== null && color.v !== '';

  const c = color.c !== undefined && color.c !== null && color.c !== '';
  const m = color.m !== undefined && color.m !== null && color.m !== '';
  const y = color.y !== undefined && color.y !== null && color.y !== '';
  const k = color.k !== undefined && color.k !== null && color.k !== '';

  if (isHexRgb && isHexCodeLength3) return 'rgbHex3';
  if (isHexRgb && isHexCodeLength6) return 'rgbHex6';
  if (isObject && r && g && b) return 'rgb';
  if (isObject && h && s && l) return 'hsl';
  if (isObject && h && s && v) return 'hsv';
  if (isObject && c && m && y && k) return 'cmyk';

  console.error('Unknown color profile for color: ', color);
  return null;
};

// ============================================================================
// ============================================================================
// RGB to HSV
// export const convertRGBtoHSV = (r: number, g: number, b: number) => {
//   const result = HSV(0, 0, 0);

//   r = r / 255;
//   g = g / 255;
//   b = b / 255;

//   const minVal = Math.min(r, g, b);
//   const maxVal = Math.max(r, g, b);
//   const delta = maxVal - minVal;

//   result.v = maxVal;

//   if (delta === 0) {
//     result.h = 0;
//     result.s = 0;
//   } else {
//     result.s = delta / maxVal;
//     const del_R = ((maxVal - r) / 6 + delta / 2) / delta;
//     const del_G = ((maxVal - g) / 6 + delta / 2) / delta;
//     const del_B = ((maxVal - b) / 6 + delta / 2) / delta;

//     if (r === maxVal) {
//       result.h = del_B - del_G;
//     } else if (g === maxVal) {
//       result.h = 1 / 3 + del_R - del_B;
//     } else if (b === maxVal) {
//       result.h = 2 / 3 + del_G - del_R;
//     }

//     if (result.h < 0) {
//       result.h += 1;
//     }
//     if (result.h > 1) {
//       result.h -= 1;
//     }
//   }

//   result.h = Math.round(result.h * 360);
//   result.s = Math.round(result.s * 100);
//   result.v = Math.round(result.v * 100);

//   return result;
// };

// ============================================================================
// ============================================================================
// HSV to RGB
// export const convertHSVtoRGB = (h: number, s: number, v: number) => {
//   const result = RGB(0, 0, 0);

//   h = h / 360;
//   s = s / 100;
//   v = v / 100;

//   let var_1, var_2, var_3, var_b, var_g, var_h, var_i, var_r;

//   if (s === 0) {
//     result.r = v * 255;
//     result.g = v * 255;
//     result.b = v * 255;
//   } else {
//     var_h = h * 6;
//     var_i = Math.floor(var_h);
//     var_1 = v * (1 - s);
//     var_2 = v * (1 - s * (var_h - var_i));
//     var_3 = v * (1 - s * (1 - (var_h - var_i)));

//     if (var_i === 0) {
//       var_r = v;
//       var_g = var_3;
//       var_b = var_1;
//     } else if (var_i === 1) {
//       var_r = var_2;
//       var_g = v;
//       var_b = var_1;
//     } else if (var_i === 2) {
//       var_r = var_1;
//       var_g = v;
//       var_b = var_3;
//     } else if (var_i === 3) {
//       var_r = var_1;
//       var_g = var_2;
//       var_b = v;
//     } else if (var_i === 4) {
//       var_r = var_3;
//       var_g = var_1;
//       var_b = v;
//     } else {
//       var_r = v;
//       var_g = var_1;
//       var_b = var_2;
//     }

//     result.r = var_r * 255;
//     result.g = var_g * 255;
//     result.b = var_b * 255;

//     result.r = Math.round(result.r);
//     result.g = Math.round(result.g);
//     result.b = Math.round(result.b);
//   }

//   return result;
// };

// ============================================================================
// ============================================================================
// CMYK to RGB
// export const convertCMYKtoRGB = (c: number, m: number, y: number, k: number) => {
//   const result = RGB(0, 0, 0);

//   c = c / 100;
//   m = m / 100;
//   y = y / 100;
//   k = k / 100;

//   result.r = 1 - Math.min(1, c * (1 - k) + k);
//   result.g = 1 - Math.min(1, m * (1 - k) + k);
//   result.b = 1 - Math.min(1, y * (1 - k) + k);

//   result.r = Math.round(result.r * 255);
//   result.g = Math.round(result.g * 255);
//   result.b = Math.round(result.b * 255);

//   return result;
// };

// ============================================================================
// ============================================================================
// RGB to CMYK
export const convertRGBtoCMYK = (r: number, g: number, b: number) => {
  const result = CMYK(0, 0, 0, 0);

  r = r / 255;
  g = g / 255;
  b = b / 255;

  result.k = Math.min(1 - r, 1 - g, 1 - b);
  result.c = (1 - r - result.k) / (1 - result.k);
  result.m = (1 - g - result.k) / (1 - result.k);
  result.y = (1 - b - result.k) / (1 - result.k);

  if (result.k === 1) {
    result.c = 0;
    result.m = 0;
    result.y = 0;
  }

  result.c = Math.round(result.c * 100);
  result.m = Math.round(result.m * 100);
  result.y = Math.round(result.y * 100);
  result.k = Math.round(result.k * 100);

  return result;
};

// ============================================================================
// ===========================================================================
// HEX to RGB
export const convertHEXtoRGB = (colorString: string): Rgb | null => {
  colorString = colorString.trim().replace(/#/, '');
  if (colorString.length === 3)
    colorString = colorString
      .split('')
      .map((symbol: any) => symbol + symbol)
      .join('');

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(colorString);

  if (result) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };
  }

  return null;
};

// ============================================================================
// ============================================================================
// RGB to HEX
export function convertRGBtoHEX(r: Rgb): string;
export function convertRGBtoHEX(r: number, g: number, b: number): string;
export function convertRGBtoHEX(r: number | Rgb, g?: number, b?: number): string {
  const colorCodeToHex = (color: any) => {
    const hex = color.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  const rgbToHex = (red: number, green: number, blue: number) => {
    return '#' + colorCodeToHex(Math.round(red)) + colorCodeToHex(Math.round(green)) + colorCodeToHex(Math.round(blue));
  };

  // Object format
  // @ts-ignore
  if (r.hasOwnProperty('r')) return rgbToHex(r.r, r.g, r.b);

  // Regular format
  // @ts-ignore
  return rgbToHex(r, g, b);
}

// ============================================================================
// ============================================================================
// Validate RGB value
export const validRgbValue = (x: any) => {
  return lodashIsFinite(x) && x > -1 && x < 256;
};

// ============================================================================
// ============================================================================
// Validate RGB valueS
export const validRgbValues = (rgb: any) => {
  return rgb && validRgbValue(rgb.r) && validRgbValue(rgb.g) && validRgbValue(rgb.b);
};

// ============================================================================
// ============================================================================
// RGB to CSS value
export const rgbToCssValue = (r: any, g: any, b: any) => {
  // Object format
  if (r.hasOwnProperty('r')) {
    g = r.g;
    b = r.b;
    r = r.r;
  }

  return `rgb(${r}, ${g}, ${b})`;
};

// ============================================================================
// ============================================================================
// Colour drafts

// A colour being edited in the picker, before it has been saved. It has no id until then,
// so __draftKey stands in for one: without it two different unsaved drafts would compare
// equal and edits to one would appear to apply to the other.
export interface ColorDraft {
  __draftKey: string;
  name: string;
  code?: string;
  groupId?: number;
  c: number;
  m: number;
  y: number;
  k: number;
  r: number;
  g: number;
  b: number;
}

let draftCounter = 0;

export const createColorDraft = (values: Partial<ColorDraft> = {}): ColorDraft => ({
  __draftKey: `draft-${++draftCounter}`,
  name: 'White',
  code: '',
  // White, because a black default makes the whole colour wheel look disabled.
  c: 0,
  m: 0,
  y: 0,
  k: 0,
  r: 255,
  g: 255,
  b: 255,
  ...values,
});

/** Copies a saved colour into an editable draft. */
export const colorToDraft = (color: ColorDto): ColorDraft =>
  createColorDraft({
    name: color.name,
    code: color.code,
    groupId: color.groupId,
    c: color.c,
    m: color.m,
    y: color.y,
    k: color.k,
    r: color.r,
    g: color.g,
    b: color.b,
  });

export const draftToValues = (draft: ColorDraft): BrandColorValues => ({
  name: draft.name,
  code: draft.code,
  groupId: draft.groupId,
  c: draft.c,
  m: draft.m,
  y: draft.y,
  k: draft.k,
  r: draft.r,
  g: draft.g,
  b: draft.b,
});

// // Functions was copied from here https://www.webtoolkit.info/javascript_color_conversion.html
//
// // ============================================================================
// // ============================================================================
// // RGB to HSV
// export const convertRGBtoHSV = (r,g,b) => {
//
//   const HSV = function(h,s,v) {
//     if (h <= 0) { h = 0; }
//     if (s <= 0) { s = 0; }
//     if (v <= 0) { v = 0; }
//
//     if (h > 360) { h = 360; }
//     if (s > 100) { s = 100; }
//     if (v > 100) { v = 100; }
//
//     this.h = h;
//     this.s = s;
//     this.v = v;
//   }
//
//   let result = new HSV(0,0,0);
//
//   r = r / 255;
//   g = g / 255;
//   b = b / 255;
//
//   let minVal = Math.min(r,g,b);
//   let maxVal = Math.max(r,g,b);
//   let delta = maxVal - minVal;
//
//   result.v = maxVal;
//
//   if (delta === 0) {
//     result.h = 0;
//     result.s = 0;
//   } else {
//     result.s = delta / maxVal;
//     let del_R = (((maxVal - r) / 6) + (delta / 2)) / delta;
//     let del_G = (((maxVal - g) / 6) + (delta / 2)) / delta;
//     let del_B = (((maxVal - b) / 6) + (delta / 2)) / delta;
//
//     if (r === maxVal) { result.h = del_B - del_G; }
//     else if (g === maxVal) { result.h = (1 / 3) + del_R - del_B; }
//     else if (b === maxVal) { result.h = (2 / 3) + del_G - del_R; }
//
//     if (result.h < 0) { result.h += 1; }
//     if (result.h > 1) { result.h -= 1; }
//   }
//
//   result.h = Math.round(result.h * 360);
//   result.s = Math.round(result.s * 100);
//   result.v = Math.round(result.v * 100);
//
//   return result;
// };
//
//
//
// // ============================================================================
// // ============================================================================
// // HSV to RGB
// export const convertHSVtoRGB = (h,s,v) => {
//
//   const RGB = function(r,g,b) {
//     if (r <= 0) { r = 0; }
//     if (g <= 0) { g = 0; }
//     if (b <= 0) { b = 0; }
//
//     if (r > 255) { r = 255; }
//     if (g > 255) { g = 255; }
//     if (b > 255) { b = 255; }
//
//     this.r = r;
//     this.g = g;
//     this.b = b;
//   }
//
//   let result = new RGB(0, 0, 0);
//
//   h = h / 360;
//   s = s / 100;
//   v = v / 100;
//
//   let var_1, var_2, var_3, var_b, var_g, var_h, var_i, var_r;
//
//   if (s === 0) {
//     result.r = v * 255;
//     result.g = v * 255;
//     result.v = v * 255;
//
//   } else {
//     var_h = h * 6;
//     var_i = Math.floor(var_h);
//     var_1 = v * (1 - s);
//     var_2 = v * (1 - s * (var_h - var_i));
//     var_3 = v * (1 - s * (1 - (var_h - var_i)));
//
//     if (var_i === 0) { var_r = v; var_g = var_3; var_b = var_1 }
//     else if (var_i === 1) { var_r = var_2; var_g = v; var_b = var_1 }
//     else if (var_i === 2) { var_r = var_1; var_g = v; var_b = var_3 }
//     else if (var_i === 3) { var_r = var_1; var_g = var_2; var_b = v; }
//     else if (var_i === 4) { var_r = var_3; var_g = var_1; var_b = v; }
//     else {var_r = v; var_g = var_1; var_b = var_2}
//
//     result.r = var_r * 255;
//     result.g = var_g * 255;
//     result.b = var_b * 255;
//
//     result.r = Math.round(result.r);
//     result.g = Math.round(result.g);
//     result.b = Math.round(result.b);
//   }
//
//   return result;
// };
//
//
//
// // ============================================================================
// // ============================================================================
// // CMYK to RGB
// export const convertCMYKtoRGB = (c,m,y,k) => {
//
//   const RGB = function(r,g,b) {
//     if (r <= 0) { r = 0; }
//     if (g <= 0) { g = 0; }
//     if (b <= 0) { b = 0; }
//
//     if (r > 255) { r = 255; }
//     if (g > 255) { g = 255; }
//     if (b > 255) { b = 255; }
//
//     this.r = r;
//     this.g = g;
//     this.b = b;
//   }
//
//   let result = new RGB(0, 0, 0);
//
//   c = c / 100;
//   m = m / 100;
//   y = y / 100;
//   k = k / 100;
//
//   result.r = 1 - Math.min( 1, c * ( 1 - k ) + k );
//   result.g = 1 - Math.min( 1, m * ( 1 - k ) + k );
//   result.b = 1 - Math.min( 1, y * ( 1 - k ) + k );
//
//   result.r = Math.round( result.r * 255 );
//   result.g = Math.round( result.g * 255 );
//   result.b = Math.round( result.b * 255 );
//
//   return result;
// };
//
//
//
// // ============================================================================
// // ============================================================================
// // RGB to CMYK
// export const convertRGBtoCMYK = (r,g,b) => {
//
//   const CMYK = function(c,m,y,k) {
//     if (c <= 0) { c = 0; }
//     if (m <= 0) { m = 0; }
//     if (y <= 0) { y = 0; }
//     if (k <= 0) { k = 0; }
//
//     if (c > 100) { c = 100; }
//     if (m > 100) { m = 100; }
//     if (y > 100) { y = 100; }
//     if (k > 100) { k = 100; }
//
//     this.c = c;
//     this.m = m;
//     this.y = y;
//     this.k = k;
//   }
//
//   let result = new CMYK(0,0,0,0);
//
//   r = r / 255;
//   g = g / 255;
//   b = b / 255;
//
//   result.k = Math.min( 1 - r, 1 - g, 1 - b );
//   result.c = ( 1 - r - result.k ) / ( 1 - result.k );
//   result.m = ( 1 - g - result.k ) / ( 1 - result.k );
//   result.y = ( 1 - b - result.k ) / ( 1 - result.k );
//
//   if ( result.k===1 ) {
//     result.c = 0;
//     result.m = 0;
//     result.y = 0;
//   }
//
//   result.c = Math.round( result.c * 100 );
//   result.m = Math.round( result.m * 100 );
//   result.y = Math.round( result.y * 100 );
//   result.k = Math.round( result.k * 100 );
//
//   return result;
// };
//
//
//
// // https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
//
// // ============================================================================
// // ============================================================================
// // RGB to HEX
// export const convertRGBtoHEX = (r,g,b) => {
//   const colorCodeToHex = color => {
//     const hex = color.toString(16);
//     return hex.length === 1 ? '0' + hex : hex;
//   }
//
//   const rgbToHex = (red, green, blue) => {
//     return '#' + colorCodeToHex(red) + colorCodeToHex(green) + colorCodeToHex(blue);
//   }
//
//   return rgbToHex(r, g, b);
// };
//
//
//
// // ============================================================================
// // ============================================================================
// // HEX to RGB
// export const convertHEXtoRGB = hex => {
//   // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
//   const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
//   hex = hex.replace(shorthandRegex, function(m, r, g, b) {
//     return r + r + g + g + b + b;
//   });
//
//   const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
//
//   return result
//     ? {r: parseInt(result[1],16), g: parseInt(result[2],16), b: parseInt(result[3],16)}
//     : null;
// };

export function parseColor(color: any) {
  if (!color) {
    return 'transparent';
  }

  if (typeof color === 'string') {
    return color;
  }

  return convertRGBtoHEX(color);
}

/**
 * Colours are shared entities, so identity is the id. Unsaved drafts fall back to their
 * draft key, which is why drafts need one.
 */
export function colorKey(color: { id?: number; __draftKey?: string } | null | undefined): string {
  if (!color) return 'none';
  if (color.id != null) return `id:${color.id}`;
  if (color.__draftKey) return `tmp:${color.__draftKey}`;
  return 'none';
}

export function isColorsEqual(
  color1: { id?: number; __draftKey?: string } | null | undefined,
  color2: { id?: number; __draftKey?: string } | null | undefined
) {
  if (!color1 || !color2) return false;
  const key1 = colorKey(color1);
  if (key1 === 'none') return false;
  return key1 === colorKey(color2);
}

// Dedupe a list of colours by identity, keeping the first occurrence of each.
export function dedupeColors<T>(colors: T[], getColor: (item: T) => any = (c) => c): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of colors) {
    const key = colorKey(getColor(item));
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }
  return result;
}
